import type { ClientMessage, ServerMessage } from '../shared/types.js';

export type MessageHandler = (msg: ServerMessage) => void;
export type ConnectionHandler = (connected: boolean) => void;

const RECONNECT_BASE_MS = 2000;
const RECONNECT_MAX_MS = 60_000;
const IDLE_DISCONNECT_MS = 10 * 60 * 1000; // 10分
const HEARTBEAT_INTERVAL_MS = 20_000; // 20秒ごとにping
const HEARTBEAT_TIMEOUT_MS = 35_000;  // 35秒以上pongなしで半開き判定 → 再接続

export class WsClient {
  private ws: WebSocket | null = null;
  private url: string;
  private onMessage: MessageHandler;
  private onConnection: ConnectionHandler;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private _connected = false;
  private reconnectAttempts = 0;
  private intentionallyClosed = false;
  private lastUserActivityAt = Date.now();
  private idleCheckTimer: ReturnType<typeof setInterval> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private lastPongAt = 0;
  private visibilityHandler: (() => void) | null = null;
  private activityHandler: (() => void) | null = null;

  constructor(url: string, onMessage: MessageHandler, onConnection: ConnectionHandler) {
    this.url = url;
    this.onMessage = onMessage;
    this.onConnection = onConnection;
    this.setupLifecycleHooks();
  }

  /**
   * タブの可視性 / ユーザー操作 を監視し、放置時に自動切断する。
   * 放置ブラウザがWebSocketを張りっぱなしにしてCloud Runのインスタンスを
   * 起動させ続け、不要な課金を発生させる事故を防ぐための仕組み。
   */
  private setupLifecycleHooks(): void {
    if (typeof document === 'undefined') return;

    // ユーザー操作を検知して lastUserActivityAt を更新
    this.activityHandler = () => {
      this.lastUserActivityAt = Date.now();
    };
    const events: (keyof DocumentEventMap)[] = ['mousedown', 'keydown', 'touchstart', 'pointerdown'];
    for (const ev of events) {
      document.addEventListener(ev, this.activityHandler, { passive: true });
    }

    // タブの表示状態が変わったら接続を切る/再開する
    this.visibilityHandler = () => {
      if (document.visibilityState === 'hidden') {
        this.disconnect('tab hidden');
      } else if (document.visibilityState === 'visible') {
        this.lastUserActivityAt = Date.now();
        this.connect();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);

    // 定期的にアイドル状態をチェック (30秒ごと)
    this.idleCheckTimer = setInterval(() => {
      if (this._connected && Date.now() - this.lastUserActivityAt > IDLE_DISCONNECT_MS) {
        console.info('[ws-client] アイドル状態のためWebSocketを切断します');
        this.disconnect('idle timeout');
      }
    }, 30_000);
  }

  connect(): void {
    // 非表示タブからは接続しない
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      return;
    }
    this.intentionallyClosed = false;

    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) {
      return;
    }

    if (this.ws) {
      this.ws.close();
    }

    this.ws = new WebSocket(this.url);

    this.ws.onopen = () => {
      this._connected = true;
      this.reconnectAttempts = 0;
      this.lastUserActivityAt = Date.now();
      this.lastPongAt = Date.now();
      this.onConnection(true);
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }
      this.startHeartbeat();
    };

    this.ws.onmessage = (event) => {
      this.lastPongAt = Date.now();
      try {
        const msg: ServerMessage = JSON.parse(event.data as string);
        if (msg.type === 'pong') return; // heartbeat応答のみ消費
        this.onMessage(msg);
      } catch {
        console.error('Failed to parse message');
      }
    };

    this.ws.onclose = () => {
      this._connected = false;
      this.stopHeartbeat();
      this.onConnection(false);
      if (!this.intentionallyClosed) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = () => {
      // onclose will fire after onerror
    };
  }

  /**
   * 明示的に接続を切る。再接続スケジュールも停止する。
   */
  disconnect(_reason?: string): void {
    this.intentionallyClosed = true;
    this.stopHeartbeat();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try { this.ws.close(); } catch { /* noop */ }
      this.ws = null;
    }
    this._connected = false;
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (!this._connected) return;
      // 半開き検知: pingを送っているのにpongが返らない
      if (Date.now() - this.lastPongAt > HEARTBEAT_TIMEOUT_MS) {
        console.info('[ws-client] heartbeat timeout → 再接続');
        if (this.ws) {
          try { this.ws.close(); } catch { /* noop */ }
          this.ws = null;
        }
        this._connected = false;
        this.stopHeartbeat();
        this.onConnection(false);
        this.scheduleReconnect();
        return;
      }
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
      this.lastUserActivityAt = Date.now();
    }
  }

  get connected(): boolean {
    return this._connected;
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.intentionallyClosed) return;
    // 非表示タブでは再接続しない
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    const delay = Math.min(
      RECONNECT_BASE_MS * Math.pow(2, this.reconnectAttempts),
      RECONNECT_MAX_MS,
    );
    this.reconnectAttempts++;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }
}

export function getWsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}
